import React, { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import CustomSelect from "./CustomSelect";
import { generateDateRangeOptions } from "utils/optionGenerators";
import {
  Section,
  ContentWrapper,
  SectionTitle,
  DropdownWrapper,
  LastUpdated,
  MetricsGrid,
  MetricItem,
  MetricNumber,
  MetricLabel
} from "./SummaryMetrics.styled";

const SummaryMetrics = ({ quarterlySummary, communityRequests }) => {
  const { t } = useTranslation();
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  const quarterOptions = useMemo(
    () => generateDateRangeOptions(communityRequests),
    [communityRequests]
  );

  const metrics = useMemo(() => {
    if (!selectedQuarter)
      return { agencies: 0, communityRequests: 0, videosShared: 0 };
    const apiKey = selectedQuarter.value.replace("-q", "-Q");
    const data = quarterlySummary.quarters?.[apiKey];
    return {
      agencies: data?.agencies ?? 0,
      communityRequests: data?.solicitations ?? 0,
      videosShared: data?.videoSubmissions ?? 0
    };
  }, [selectedQuarter, quarterlySummary]);

  const handleQuarterChange = useCallback(option => {
    setSelectedQuarter(option);
  }, []);

  useEffect(() => {
    if (quarterOptions.length && !selectedQuarter) {
      setSelectedQuarter(quarterOptions[0]);
    }
  }, [quarterOptions]);

  return (
    <Section>
      <ContentWrapper>
        <SectionTitle>{t("impactReport.summaryMetrics.title")}</SectionTitle>
        <DropdownWrapper>
          <CustomSelect
            value={selectedQuarter}
            onChange={handleQuarterChange}
            options={quarterOptions}
            placeholder={t("impactReport.summaryMetrics.selectQuarter")}
            isClearable={false}
          />
        </DropdownWrapper>
        <LastUpdated>
          {quarterlySummary.lastUpdated
            ? `${t("impactReport.summaryMetrics.lastUpdated")} ${new Date(
                quarterlySummary.lastUpdated
              ).toLocaleDateString()}`
            : t("impactReport.summaryMetrics.lastUpdated")}
        </LastUpdated>
        <MetricsGrid>
          <MetricItem>
            <MetricNumber>{metrics.agencies}</MetricNumber>
            <MetricLabel>
              {t("impactReport.summaryMetrics.agencies")}
            </MetricLabel>
          </MetricItem>
          <MetricItem>
            <MetricNumber>{metrics.communityRequests}</MetricNumber>
            <MetricLabel>
              {t("impactReport.summaryMetrics.communityRequests")}
            </MetricLabel>
          </MetricItem>
          <MetricItem>
            <MetricNumber>{metrics.videosShared}</MetricNumber>
            <MetricLabel>
              {t("impactReport.summaryMetrics.videosShared")}
            </MetricLabel>
          </MetricItem>
        </MetricsGrid>
      </ContentWrapper>
    </Section>
  );
};

SummaryMetrics.propTypes = {
  quarterlySummary: PropTypes.object,
  communityRequests: PropTypes.array
};

export default SummaryMetrics;
